import React from "react";
import { useActionData, useSubmit, useNavigation } from "react-router";
import { useFormContext, useForm, FormProvider } from "react-hook-form";

import { createFormData, mergeErrors } from "../utilities";

import type { FetcherWithComponents, SubmitFunction } from "react-router";
import type { DeepPartial, FieldValues, Path, RegisterOptions, UseFormHandleSubmit, UseFormProps, UseFormReturn, SubmitErrorHandler, SubmitHandler } from "react-hook-form";

export type SubmitFunctionOptions = Parameters<SubmitFunction>[1];

export interface UseRemixFormOptions<T extends FieldValues> extends UseFormProps<T> {
  submitHandlers?: {
    onValid?: SubmitHandler<T>;
    onInvalid?: SubmitErrorHandler<T>;
  };
  submitConfig?: SubmitFunctionOptions;
  submitData?: FieldValues;
  fetcher?: FetcherWithComponents<T>;
  includesFile?: boolean;
}

export const useRemixForm = <T extends FieldValues>({ submitHandlers, submitConfig, submitData, fetcher, includesFile, ...formProps }: UseRemixFormOptions<T>) => {
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = React.useState(false);
  const actionSubmit = useSubmit();
  const actionData: any = useActionData();
  const submit = fetcher?.submit ?? actionSubmit;
  const data: any = (fetcher?.data?.result || fetcher?.data) ?? (actionData?.result || actionData);
  const methods = useForm<T>(formProps);
  const navigation = useNavigation();
  // Either it's submitted to an action or submitted to a fetcher (or neither)
  const isSubmittingForm = navigation.state !== "idle" || (fetcher && fetcher.state !== "idle");

  // Submits the data to the server when form is valid
  const onSubmit = (data: T) => {
    setIsSubmittedSuccessfully(true);
    const formData = createFormData({ ...data, ...submitData });
    submit(formData, {
      method: "post",
      ...(includesFile && { encType: "multipart/form-data" }),
      ...submitConfig,
    });
  };
  const values = methods.getValues();
  const validKeys = Object.keys(values);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onInvalid = () => {};

  const { formState } = methods;

  const { dirtyFields, isDirty, isSubmitSuccessful, isSubmitted, isSubmitting, isValid, isValidating, touchedFields, submitCount, errors, isLoading } = formState;

  const formErrors = mergeErrors<T>(errors, data?.errors ? data.errors : null, validKeys);

  // // kept for figuring out everytime this issue occurs
  // formErrors && JSON.stringify(formErrors) !== "{}" && console.log({ formErrors });

  return {
    ...methods,
    handleSubmit: methods.handleSubmit(submitHandlers?.onValid ?? onSubmit, submitHandlers?.onInvalid ?? onInvalid),
    reset: (values?: T | DeepPartial<T> | undefined) => {
      setIsSubmittedSuccessfully(false);
      methods.reset(values as T);
    },
    register: (
      name: Path<T>,
      options?: RegisterOptions<T> & {
        disableProgressiveEnhancement?: boolean;
      },
    ) => ({
      ...methods.register(name, options),
      ...(!options?.disableProgressiveEnhancement && {
        defaultValue: data?.defaultValues?.[name] ?? "",
      }),
    }),
    formState: {
      dirtyFields,
      isDirty,
      isSubmitSuccessful: isSubmittedSuccessfully || isSubmitSuccessful,
      isSubmitted,
      isSubmitting: isSubmittingForm || isSubmitting,
      isValid,
      isValidating,
      touchedFields,
      submitCount,
      isLoading,
      errors: formErrors,
    },
  };
};
interface RemixFormProviderProps<T extends FieldValues> extends Omit<UseFormReturn<T>, "handleSubmit"> {
  children: React.ReactNode;
  handleSubmit: any;
  register: any;
}
export const RemixFormProvider = <T extends FieldValues>({ children, ...props }: RemixFormProviderProps<T>) => <FormProvider {...props}>{children}</FormProvider>;

export const useRemixFormContext = <T extends FieldValues>() => {
  const methods = useFormContext<T>();

  return {
    ...methods,
    handleSubmit: methods.handleSubmit as any as ReturnType<UseFormHandleSubmit<T>>,
  };
};
